import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import * as S from "./styles";
import { useKOGImageZoom } from "./useZoomableImage";
import KOGIconCloseModal from "../kogIconCloseModal/kogIconCloseModal";

interface KOGImageZoomProps {
	src: string;
	alt: string;
	onErrorImg?: React.ReactEventHandler<HTMLImageElement>;
}

const KOGImageZoom: React.FC<KOGImageZoomProps> = ({
  src,
  alt,
  onErrorImg = () => {}
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    isFullscreen,
    scale,
    position,
    isDragging,
    isCentering,
    imageRef,
    containerRef,
    openFullscreen,
    closeFullscreen,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleOverlayClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    zoomIn,
    zoomOut
  } = useKOGImageZoom();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen && e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeFullscreen();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown, { capture: true });
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen, closeFullscreen]);

  const MIN_SCALE = 1;
  const MAX_SCALE = 5;

  const getTransition = () => {
    if (isDragging) {
      return 'none';
    }

    if (isCentering) {
      return 'transform 0.3s ease-out';
    }

    return 'transform 0.1s ease-out';
  };

  return (
    <>
      <S.ThumbnailWrapper
        ref={wrapperRef}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          openFullscreen();
        }}
        onMouseDown={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}>
        <S.ThumbnailImage
          src={src}
          alt={alt}
          draggable={false}
          onError={onErrorImg}
        />
      </S.ThumbnailWrapper>

      {isFullscreen &&
        createPortal(
          <S.FullscreenOverlay onClick={handleOverlayClick} ref={containerRef}>
            <S.ZoomableImageContainer
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                cursor:
                  scale > MIN_SCALE
                    ? isDragging
                      ? 'grabbing'
                      : 'grab'
                    : 'default'
              }}>
              <S.ZoomedImage
                ref={imageRef}
                src={src}
                alt={alt}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transition: getTransition()
                }}
                draggable={false}
                onError={onErrorImg}
              />
            </S.ZoomableImageContainer>

            <KOGIconCloseModal handleClose={closeFullscreen} />

            <S.ZoomIndicator>
              <S.ZoomButton onClick={zoomOut} disabled={scale <= MIN_SCALE}>
                −
              </S.ZoomButton>
              <S.ZoomPercentage>{Math.round(scale * 100)}%</S.ZoomPercentage>
              <S.ZoomButton onClick={zoomIn} disabled={scale >= MAX_SCALE}>
                +
              </S.ZoomButton>
            </S.ZoomIndicator>
          </S.FullscreenOverlay>,
          document.body
        )}
    </>
  );
};

export default KOGImageZoom;
